import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import {
    Box,
    Button,
    Divider,
    Menu,
    MenuItem,
    Typography,
    ListItemText,
    Chip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import SettingsIcon from "../../../components/icons/SettingsIcon";
import { getEnvironments, switchEnvironment } from "../../../actions/environmentAction";
import {
    PRODUCTION_SLUG,
    clearEnvironmentScopedViewState,
} from "../../../utils/environment";

/**
 * Environment picker for the workspace the user is in.
 *
 * Production is styled deliberately differently from every other environment:
 * the whole point of a staging environment is that edits made there are not
 * live, and an editor has to be able to tell which one they are typing into at
 * a glance.
 */
const EnvironmentSwitcher = () => {
    const dispatch = useDispatch();
    const { workspaceId } = useParams();
    const [anchorEl, setAnchorEl] = useState(null);

    const loadedWorkspaceId = useSelector((state) => state.environments.workspaceId);
    const isCurrentWorkspace = loadedWorkspaceId === workspaceId;
    // While another workspace's list is still in state, show nothing rather
    // than an environment that does not belong to the workspace on screen.
    const environments = useSelector((state) =>
        isCurrentWorkspace ? state.environments.environments : [],
    );
    const active = useSelector((state) =>
        isCurrentWorkspace ? state.environments.active : null,
    );

    useEffect(() => {
        if (workspaceId && workspaceId !== loadedWorkspaceId) {
            dispatch(getEnvironments(workspaceId));
        }
    }, [dispatch, workspaceId, loadedWorkspaceId]);

    if (!workspaceId) return null;

    const isProduction = !active || active.isDefault;
    const label = active?.name ?? "Production";

    const handleSelect = (environment) => {
        setAnchorEl(null);
        if (!environment || environment.slug === (active?.slug ?? PRODUCTION_SLUG)) return;

        // Remember the choice first — the reload below reads it back.
        dispatch(switchEnvironment(workspaceId, environment));
        clearEnvironmentScopedViewState(workspaceId);

        // Start the user over at content models. Which models exist, their ids,
        // and every entry id beneath them are all different in another
        // environment, so whatever was on screen has no counterpart here.
        // A full page load rather than a client-side navigation: it also drops
        // the previous environment's content out of Redux, which would
        // otherwise stay on screen until each new fetch happened to land.
        window.location.assign(`/${workspaceId}/content-models`);
    };

    const accent = isProduction
        ? { fg: "#0f766e", bg: "rgba(13, 148, 136, 0.10)", border: "rgba(13, 148, 136, 0.35)" }
        : { fg: "#b45309", bg: "rgba(245, 158, 11, 0.12)", border: "rgba(245, 158, 11, 0.45)" };

    return (
        <>
            <Button
                onClick={(event) => setAnchorEl(event.currentTarget)}
                endIcon={<KeyboardArrowDownIcon sx={{ fontSize: "18px !important" }} />}
                sx={{
                    textTransform: "none",
                    color: accent.fg,
                    backgroundColor: accent.bg,
                    border: `1px solid ${accent.border}`,
                    borderRadius: "12px",
                    px: 1.5,
                    py: 0.75,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    "&:hover": { backgroundColor: accent.bg, borderColor: accent.fg },
                }}
            >
                <Box
                    component="span"
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: accent.fg,
                        mr: 1,
                        flexShrink: 0,
                    }}
                />
                {label}
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                transformOrigin={{ horizontal: "left", vertical: "top" }}
                anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        mt: 1,
                        minWidth: 260,
                        borderRadius: "12px",
                        filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.12))",
                    },
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ px: 2, py: 1, display: "block", color: "#94a3b8", fontWeight: 600 }}
                >
                    ENVIRONMENT
                </Typography>

                {environments.map((environment) => {
                    const selected = environment.slug === (active?.slug ?? "production");
                    return (
                        <MenuItem
                            key={environment.slug}
                            selected={selected}
                            onClick={() => handleSelect(environment)}
                            sx={{ py: 1 }}
                        >
                            <ListItemText
                                primary={
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography variant="body2" fontWeight={selected ? 600 : 500}>
                                            {environment.name}
                                        </Typography>
                                        {environment.isDefault && (
                                            <Chip
                                                label="default"
                                                size="small"
                                                sx={{ height: 18, fontSize: "10px", color: "#0f766e", backgroundColor: "rgba(13,148,136,0.12)" }}
                                            />
                                        )}
                                    </Box>
                                }
                                secondary={
                                    <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                                        {environment.slug}
                                    </Typography>
                                }
                            />
                            {selected && <CheckIcon sx={{ fontSize: 18, color: "#4f46e5", ml: 1 }} />}
                        </MenuItem>
                    );
                })}

                <Divider sx={{ my: 0.5 }} />

                <MenuItem
                    component={Link}
                    to={`/${workspaceId}/settings`}
                    onClick={() => setAnchorEl(null)}
                    sx={{ py: 1, gap: 1 }}
                >
                    <Box component="span" sx={{ display: "flex" }}>
                        <SettingsIcon size={18} />
                    </Box>
                    <Typography variant="body2">Manage environments</Typography>
                </MenuItem>
            </Menu>
        </>
    );
};

export default EnvironmentSwitcher;
